import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useChangeGroupFaqMutation } from "entities/faqs/mutations/useChangeGroupFaqMutation";
import { useDeleteFaqMutation } from "entities/faqs/mutations/useDeleteFaqMutation";
import { useReorderFaqsListMutation } from "entities/faqs/mutations/useReorderFaqsListMutation";
import { useUpdateFaqStatusMutation } from "entities/faqs/mutations/useUpdateFaqStatusMutation";
import { useFaqGroupsQuery } from "entities/faqs/queries/useFaqGroupsQuery";
import { useFaqsListByFaqGroupQuery } from "entities/faqs/queries/useFaqsListByFaqGroupQuery";
import { FaqData, FaqDataToCreate } from "entities/faqs/types";
import { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { useModalState } from "shared/hooks/useModalState";
import { createContext } from "shared/utils/context";
import { ColumnsProps } from "../types";
import { FaqsType } from "./types";

const [Provider, useFaqs] = createContext<FaqsType>({
  name: "FaqsContext",
  hookName: "useFaqs",
  providerName: "FaqsProvider",
});

const FaqsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { id } = useParams();
  const faqDetailsDrawer = useDrawerState<string>();
  const deleteModalState = useModalState();
  const changeGroupModalState = useModalState();

  const faqGroupId = String(id);
  const { data: faqsList = [], isLoading } =
    useFaqsListByFaqGroupQuery(faqGroupId);

  const { data: faqGroups = [] } = useFaqGroupsQuery();

  const { mutate: mutateReorderFaqsList, isPending: isPendingReorder } =
    useReorderFaqsListMutation(faqGroupId);

  const { mutate: mutateFaqStatus, isPending: isFaqStatusChanging } =
    useUpdateFaqStatusMutation();

  const { mutate: mutateDeleteFaq, isPending: isLoadingDeleteFaqItem } =
    useDeleteFaqMutation();

  const { mutate: mutateChangeFaqGroup, isPending: isFaqGroupChanging } =
    useChangeGroupFaqMutation();

  const handleOpenDeleteModal = (faqData: FaqData) => {
    deleteModalState.openModal(faqData);
  };

  const handleOpenChangeGroupModal = (faqData: FaqData) => {
    changeGroupModalState.openModal(faqData);
  };

  const handleDeleteFaqItem = () => {
    mutateDeleteFaq({
      faqGroupId,
      faqId: deleteModalState.modalDetails.id,
      faqsList,
      onSuccess: deleteModalState.closeModal,
    });
  };

  const onUpdateStatus = (faqData: FaqData) => {
    mutateFaqStatus({
      faqGroupId,
      faqId: faqData.id,
      status: faqData.status,
    });
  };

  const handleChangeFaqGroup = (faqData: FaqData, targetFaqGroupId: string) => {
    const dataToSubmit: FaqDataToCreate = {
      question: faqData.question,
      answer: faqData.answer,
      order: 0,
      status: faqData.status,
    };

    mutateChangeFaqGroup({
      targetFaqGroupId,
      data: dataToSubmit,
      faqId: faqData.id,
    });

    mutateDeleteFaq({
      faqGroupId,
      faqId: faqData.id,
      faqsList,
      onSuccess: changeGroupModalState.closeModal,
    });
  };

  const writeAccess = useRolePermission("faq:write");
  const columnsParams: ColumnsProps = {
    handlePublishStatus: onUpdateStatus,
    handleOpenDeleteModal,
    handleOpenChangeGroupModal,
    faqItemDrawer: faqDetailsDrawer,
    readOnly: !writeAccess.allowed,
  };

  const faqsGroupTitle =
    faqGroups.find(({ id }) => id === faqGroupId)?.title ?? "";

  const value = {
    data: faqsList,
    isLoadingFaqs: isLoading || isLoadingDeleteFaqItem || isFaqStatusChanging,
    isLoadingReorderList: isPendingReorder,
    faqsGroupTitle,
    faqDetailsDrawer,
    mutateReorderFaqsList,
    columnsParams,
    faqGroupId,
    changeGroupModalState,
    deleteModalState,
    handleDeleteFaqItem,
    handleChangeFaqGroup,
    isLoadingDeleteFaqItem,
    isFaqGroupChanging,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { FaqsProvider, useFaqs };
